import { FC, useContext, useState } from "react";
import { useParams } from "react-router-dom";

import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
} from "@mui/material";
import { Form, Formik } from "formik";
import moment from "moment";
import rehypeRaw from "rehype-raw";

import { SATOSContentRender } from "@/components/Application/SATOSContentRender";
import { SATOSLink } from "@/components/Application/SATOSLink";
import {
	GetLanguageValueAsString,
	Translate,
} from "@/components/Application/Translate";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import { CloseButton } from "@/helpers/components/CloseButton";
import CustomCheckbox from "@/helpers/components/CustomFormFields/CustomCheckbox";
import CustomInput from "@/helpers/components/CustomFormFields/CustomInput";
import CustomSelect from "@/helpers/components/CustomFormFields/CustomSelect";
import CustomTextArea from "@/helpers/components/CustomFormFields/CustomTextarea";

import { Language } from "@/model/Language/Language";
import { SaveProcedureRequest } from "@/model/Procedure/ProcedureSaveRequest";
import { TranslationContext } from "@/model/Translation/TranslationContext";
import { UserContext } from "@/model/User/UserContext";

import { useHideShift } from "@/service/Hooks";
import { SaveAndResetProcedure } from "@/service/ProcedureMutations";
import {
	useFetchActiveProcedure,
	useFetchProcedureShiftConfiguration,
} from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

interface CompleteProcedureModalProps {
	open: boolean;
	onClose: () => void;
}

export const CompleteProcedure: FC<CompleteProcedureModalProps> = ({
	open,
	onClose,
}) => {
	const translationContext = useContext(TranslationContext);
	const { id } = useParams<RouteParams>();
	const user = useContext(UserContext);
	const [shiftText, setShiftText] = useState(getShiftText(translationContext));

	const { data: procedure } = useFetchActiveProcedure();
	const { data: procedureShiftConfiguration } =
		useFetchProcedureShiftConfiguration();

	const hideShift = useHideShift();

	const saveAndResetProcedure = SaveAndResetProcedure();

	const save = async (values: SaveProcedureRequest) => {
		try {
			if (values.CreateShiftEntryRequest && values.ProcedureName) {
				values.CreateShiftEntryRequest.ShiftText = getRenderedShiftText(
					shiftText,
					values.ProcedureName,
					user?.Name,
				);
			}
			await saveAndResetProcedure(values);
			AlertRequest.success();
		} catch (error) {
			AlertRequest.failed();
			console.error(error);
		} finally {
			onClose();
		}
	};

	if (!procedure || !procedureShiftConfiguration) {
		return <></>;
	}

	return (
		<>
			<Dialog open={open} fullWidth onClose={onClose}>
				<DialogTitle>
					<Translate
						String="Procedure.CompleteReset.SaveProcedure"
						HTMLTag="h4"
						className="heading"
					>
						Save Procedure
					</Translate>
					<CloseButton onClose={onClose} />
				</DialogTitle>
				<DialogContent>
					<Formik
						initialValues={{
							ProcedureId: +id,
							ProcedureName: procedure?.Name,
							ProcedureDescription: "",
							Reset: true,
							Responsible: user?.Id,
							ToShift: false,
							CreateShiftEntryRequest: {
								GroupId:
									procedureShiftConfiguration?.Groups.find((sys) => sys.Default)
										?.Id || 0,
								SystemId:
									procedureShiftConfiguration?.Systems.find(
										(sys) => sys.Default,
									)?.Id || 0,
								ShiftText: getRenderedShiftText(
									shiftText,
									procedure?.Name,
									user?.Name,
								),
								Tags: [],
								Handover: false,
							},
						}}
						onSubmit={save}
					>
						{({ values }) => (
							<Form>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<CustomInput
											label={"Name of procedure"}
											name={"ProcedureName"}
											type={"text"}
											id={"ProcedureName"}
											translationString="ProcedureName"
										/>
									</Grid>
									<Grid item xs={12}>
										<CustomTextArea
											style={{ width: "100%" }}
											label={"Description"}
											name={"ProcedureDescription"}
											id={"ProcedureDescription"}
											translationString="Description"
										/>
									</Grid>
									<Grid item xs={12}>
										<CustomSelect
											style={{ width: "100%", height: "40px" }}
											label={"Responsible"}
											name={"Responsible"}
											id={"Responsible"}
											translationString="Responsible"
										>
											{procedureShiftConfiguration.ResponsibleUsers.map(
												(user) => (
													<option value={user.Id} title={user.UserName}>
														{user.Name}
													</option>
												),
											)}
										</CustomSelect>
									</Grid>
									{!hideShift && (
										<Grid item xs={12}>
											<CustomCheckbox
												label={"To Shiftlog"}
												name={"ToShift"}
												id={"ToShift"}
												type={"checkbox"}
												translationString="ToShiftlog"
											/>
										</Grid>
									)}
									{values.ToShift && !hideShift && (
										<>
											<Grid item xs={12}>
												<ShiftGroups />
											</Grid>
											<Grid item xs={12}>
												<CustomTextArea
													style={{ width: "100%" }}
													multiline
													rows={5}
													label={"Shift text"}
													name={"CreateShiftEntryRequest.ShiftText"}
													id={"ShiftText"}
													translationString="ShiftText"
												/>
											</Grid>
										</>
									)}
								</Grid>
								<DialogActions>
									<Button
										variant="outlined"
										color="warning"
										startIcon={<CloseIcon />}
										onClick={onClose}
										style={{ height: "40px" }}
									>
										Close
									</Button>
									<Button
										variant="outlined"
										color="success"
										autoFocus
										type="submit"
										value="Lag ny node"
										style={{ height: "40px" }}
										startIcon={<SaveIcon />}
									>
										<Translate
											String="Procedure.CompleteReset.SaveAndReset"
											className="heading"
										>
											Save and reset procedure
										</Translate>
									</Button>
								</DialogActions>
							</Form>
						)}
					</Formik>
				</DialogContent>
			</Dialog>
			<CompleteProcedureJobStatus />
		</>
	);
};

const CompleteProcedureJobStatus: FC = () => {
	const { jobid } = useParams<RouteParams>();

	if (!jobid) {
		return <></>;
	}

	return (
		<div>
			<SATOSLink className="  " to="AccessProcedure" params={[jobid]}>
				{jobid}
			</SATOSLink>
		</div>
	);
};

interface IShiftGroups {}

const ShiftGroups: FC<IShiftGroups> = () => {
	const { data: procedureShiftConfiguration } =
		useFetchProcedureShiftConfiguration();

	return (
		<Grid container spacing={2}>
			<Grid item xs={6}>
				<CustomSelect
					style={{ height: "40px" }}
					label={"System"}
					name={"CreateShiftEntryRequest.SystemId"}
					id={"SystemId"}
				>
					{procedureShiftConfiguration?.Systems.map((system) => (
						<option value={system.Id}>{system.Name}</option>
					))}
				</CustomSelect>
			</Grid>
			<Grid item xs={6}>
				<CustomSelect
					style={{ height: "40px" }}
					label={"Group"}
					name={"CreateShiftEntryRequest.GroupId"}
					id={"GroupId"}
				>
					{procedureShiftConfiguration?.Groups.map((group) => (
						<option value={group.Id}>{group.Name}</option>
					))}
				</CustomSelect>
			</Grid>
		</Grid>
	);
};

interface IShiftDescription {
	procedureName: string;
	description: string;
	setDescription: (description: string) => void;
}

const ShiftDescription: FC<IShiftDescription> = ({
	procedureName,
	description,
	setDescription,
}) => {
	const user = useContext(UserContext);

	return (
		<>
			<div className="row-one-column row">
				<div>
					<label>Shift text</label>
					<textarea
						rows={5}
						onChange={(e) => setDescription(e.target.value)}
						value={description}
					/>
				</div>
			</div>
			<div className="row-one-column row">
				<div>
					<div>
						<SATOSContentRender
							rehypePlugins={[rehypeRaw]}
							content={getRenderedShiftText(
								description,
								procedureName,
								user?.Name,
							)}
						/>
						{}
					</div>
				</div>
			</div>
		</>
	);
};

function getShiftText(translationContext: Language | null): string {
	return GetLanguageValueAsString(
		translationContext,
		"Procedure.CompleteReset.ToShift.DefaultShiftText",
		"{procedurename} was completed at {timestamp} by {user}",
	);
}

function getRenderedShiftText(
	text: string,
	procedureName: string,
	userName: string | undefined,
): string {
	const replacementRules = {
		procedurename: procedureName,
		timestamp: moment(Date.now()).format("DD/MM/YY  HH:mm"),
		user: userName ?? "",
	};

	function replaceTextTokens(
		source: string,
		replacements: { [name: string]: string },
	) {
		return source.replace(new RegExp("{([A-z]*)}", "g"), (m) => {
			return replacements[m.substring(1, m.length - 1)];
		});
	}

	return replaceTextTokens(text, replacementRules);
}
